import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { headingText, isHeadingBlock } from "@/lib/seo";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

export default function ArticlePage() {
  const { articleId } = useParams();
  const { content } = useContent();
  const { book } = useBooking();
  const article = (content?.articles || []).find((a) => a.id === articleId);

  if (!content) return <div className="min-h-screen" />;
  if (!article)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 pt-32 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-4xl font-semibold text-[#1A1615]">Article not found</h1>
        <Link to="/blog" data-testid="article-back-link" className="btn-ghost-lux mt-6 px-6 py-3 text-sm">
          Back to the Journal
        </Link>
      </div>
    );

  return (
    <article className="pb-24 pt-44" data-testid="article-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-8">
        <Reveal>
          <Link to="/blog" data-testid="article-back-to-journal" className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-[#B38B2D] transition-colors hover:text-[#8a6d1f]">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> The Skin Journal
          </Link>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#A39288]">
            <span className="text-[#B38B2D]">{article.category}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{article.read_time}</span>
            {article.published_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h1 data-testid="article-title" className="mt-5 font-serif text-4xl font-semibold leading-[1.1] tracking-tight text-[#1A1615] sm:text-5xl">
            {article.title}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 flex items-center gap-2 text-sm font-medium text-[#5A504B]">
            <User className="h-4 w-4 text-[#B38B2D]" aria-hidden="true" /> {article.author}
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <img src={article.image} alt={article.title} className="mt-10 w-full rounded-[2rem] object-cover shadow-[0_20px_60px_rgba(26,22,21,0.15)]" data-testid="article-image" />
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 space-y-6" data-testid="article-content">
            {(article.content || []).map((p, i) =>
              isHeadingBlock(p) ? (
                <h2 key={i} className="pt-4 font-serif text-2xl font-semibold text-[#1A1615] sm:text-3xl">{headingText(p)}</h2>
              ) : (
                <p key={i} className={`font-light leading-relaxed text-[#3d3530] ${i === 0 ? "font-serif text-xl sm:text-2xl" : "text-base"}`}>
                  {p}
                </p>
              )
            )}
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          {(content?.settings?.section_visibility?.article_cta ?? true) && (
          <div className="glass mt-14 rounded-[2rem] p-8 text-center" data-testid="article-cta">
            <p className="eyebrow">Personalised Advice</p>
            <h2 className="mt-3 font-serif text-2xl font-semibold text-[#1A1615]">Your skin deserves a plan, not a guess</h2>
            <p className="mx-auto mt-3 max-w-md text-sm font-light text-[#5A504B]">
              Book a consultation with Dr. Javeria Atif at Dubai Healthcare City and get a protocol designed for your skin.
            </p>
            <button onClick={() => book()} data-testid="article-book-button" className="btn-gold mt-6 px-8 py-3.5 text-sm">
              Book Your Consultation
            </button>
          </div>
          )}
        </Reveal>
      </div>
    </article>
  );
}
